import React, { useEffect, useState } from 'react';
import { Button } from '../../styles/_app.style';
import { Field, Label, Control, InlineWrapper, AddSection, LightButton, GroupContainer, GroupHeader, GroupTitle, EntryRow, TotalScoreContainer, TotalScoreValue, TotalScoreLabel, TotalScoreBreakdown } from '../../styles/login.style';
import QualificationsDropdown from '../../components/register/QualificationDropdown';
import { ProfilePatch } from '../../hooks/auth.service';
import { Plus, Trash2 } from 'lucide-react';
import { styled } from '../../styles/stitches.config';
import { ActionButton } from '../../styles/profile/profile.style';
import StyledDropdown, { OptionGroup } from '../../components/register/StyledDropdown';
import SubjectCombobox from '../../components/common/SubjectCombobox';
import { Separator } from '../../styles/profile/form.style';
import { aLevelGrades, aLevelSubjectOptions, apGrades, apSubjectOptions, cambridgePreUGrades, cambridgePreUSubjectOptions, gcseGrades, gcseSubjectOptions, hkdseGrades, ibGrades, ibSubjectOptions, indianSchoolCertificateGrades, irishLeavingCertGrades, irishLeavingCertSubjectOptions, qualificationOptions, scottishHighersGrades, scottishHighersSubjectOptions, singaporeGceALevelGrades } from '../../types/schemas/subjectsandgrades';

type SubjectEntry = {
  id: string; // Unique ID for this specific row
  subject: string;
  grade: string;
};

// A group of qualifications of the same type
export type QualificationGroup = {
  id: string; // Unique ID for the entire group
  type: string; // 'A-Level', 'GCSE', etc.
  entries?: SubjectEntry[];
  score?: string;
};

const bonusPointsMatrix = {
    'A': { 'A': 3, 'B': 3, 'C': 2, 'D': 2 },
    'B': { 'A': 3, 'B': 2, 'C': 1, 'D': 1 },
    'C': { 'A': 2, 'B': 1, 'C': 1, 'D': 0 },
    'D': { 'A': 2, 'B': 1, 'C': 0, 'D': 0 },
};

type IbCoreGrade = keyof typeof bonusPointsMatrix;

const calculateIbTotal = (group: QualificationGroup): { score: number | null, message: string } => {
    if (!group.entries || group.entries.length === 0) {
        return { score: null, message: 'Add subjects to calculate score.' };
    }

    const tokEntry = group.entries.find(e => e.subject === 'Theory of Knowledge');
    const eeEntry = group.entries.find(e => e.subject === 'Extended Essay');

    if (!tokEntry || !eeEntry || !tokEntry.grade || !eeEntry.grade) {
        return { score: null, message: 'Select grades for TOK and EE.' };
    }

    if (tokEntry.grade === 'E' || eeEntry.grade === 'E') {
        return { score: null, message: 'A grade of E in TOK or EE results in failure.' };
    }
    const tokGrade = tokEntry.grade as IbCoreGrade;
    const eeGrade = eeEntry.grade as IbCoreGrade;

    const bonusPoints = bonusPointsMatrix[tokGrade]?.[eeGrade] ?? 0;

    const subjectEntries = group.entries.filter(
        e => e.subject !== 'Theory of Knowledge' && e.subject !== 'Extended Essay'
    );

    let subjectPoints = 0;
    for (const entry of subjectEntries) {
        const gradePoint = parseInt(entry.grade, 10);
        if (!isNaN(gradePoint)) {
            subjectPoints += gradePoint;
        }
    }
    
    const totalScore = subjectPoints + bonusPoints;
    
    return { score: totalScore, message: `(${subjectPoints} subject points + ${bonusPoints} bonus points)`};
};



// --- Component Props ---
interface Props {
  data: ProfilePatch;
  onChange: (d: ProfilePatch) => void;
  onValidityChange: (ok: boolean) => void;
}

// --- The Wizard Step Component ---
export default function QualificationsStep({
  data,
  onChange,
  onValidityChange,
}: Props) {
  const { qualificationGroups = [] } = data;
  const [typeToAdd, setTypeToAdd] = useState('A-Level');


  const getQualificationDetails = (type: string) => {
    for (const group of qualificationOptions) {
        const option = group.options.find(opt => opt.value === type);
        if (option) return option;
    }
    return null;
  };

const getSubjectOptions = (type: string): OptionGroup[] => {
    switch (type) {
        case 'GCSE':
            return gcseSubjectOptions;
        case 'A-Level':
            return aLevelSubjectOptions;
        case 'Scottish Highers':
            return scottishHighersSubjectOptions;
        case 'Cambridge Pre-U':
            return cambridgePreUSubjectOptions;
        case 'AP':
            return apSubjectOptions;
        case 'Irish Leaving Certificate':
            return irishLeavingCertSubjectOptions;
        case 'IB Diploma':
            return ibSubjectOptions
        default:
            return [];
    }
  };

  const getGradeOptions = (type: string): OptionGroup[] => {
    let grades: string[] = [];
    switch (type) {
        case 'IB Diploma':
            grades = ibGrades;
            break;
        case 'A-Level':
            grades = aLevelGrades;
            break;
        case 'GCSE':
            grades = gcseGrades;
            break;
        case 'AP':
            grades = apGrades;
            break;
        case 'IB Subject': // You had this, so I've kept it
            grades = ibGrades;
            break;
        case 'Scottish Highers':
            grades = scottishHighersGrades;
            break;
        case 'Cambridge Pre-U':
            grades = cambridgePreUGrades;
            break;
        case 'Irish Leaving Certificate':
            grades = irishLeavingCertGrades;
            break;
        case 'HKDSE':
            grades = hkdseGrades;
            break;
        case 'Indian School Certificate':
            grades = indianSchoolCertificateGrades;
            break;
        case 'Singapore GCE A-Level':
            grades = singaporeGceALevelGrades;
            break;
        default:
            grades = []; // No predefined grades, allow free-text input.
    }

    if (grades.length === 0) return [];

    return [{
        label: 'Available Grades',
        options: grades.map(g => ({ value: g, label: g }))
    }];
  };

  // --- State Handlers ---

  const addGroup = () => {
    // Prevent adding a type that already exists (e.g. only one GCSE section)
    if (qualificationGroups.some(g => g.type === typeToAdd)) {
        // You could add a toast notification here to inform the user
        return;
    }
    
    const newGroup: QualificationGroup = {
      id: crypto.randomUUID(),
      type: typeToAdd,
      entries: [{ id: crypto.randomUUID(), subject: '', grade: '' }], // Start with one blank entry
    };
    onChange({ ...data, qualificationGroups: [...qualificationGroups, newGroup] });
  };

  const removeGroup = (groupId: string) => {
    onChange({ ...data, qualificationGroups: qualificationGroups.filter(g => g.id !== groupId) });
  };

  const addSubjectToGroup = (groupId: string) => {
    const newGroups = qualificationGroups.map(g => {
      if (g.id === groupId) {
        const newEntry: SubjectEntry = { id: crypto.randomUUID(), subject: '', grade: '' };
        const existingEntries = g.entries ?? [];
        return { ...g, entries: [...existingEntries, newEntry] };
      }
      return g;
    });
    onChange({ ...data, qualificationGroups: newGroups });
  };
  
  const updateSubjectEntry = (groupId: string, entryId: string, field: 'subject' | 'grade', value: string) => {
    const newGroups = qualificationGroups.map(g => {
        if (g.id === groupId) {
            if (!g.entries) return g; 
            const newEntries = g.entries.map(e => e.id === entryId ? { ...e, [field]: value } : e);
            return { ...g, entries: newEntries };
        }
        return g;
    });
    onChange({ ...data, qualificationGroups: newGroups });
  };

  const updateSingleScore = (groupId: string, value: string) => {
    const newGroups = qualificationGroups.map(g => {
        if (g.id === groupId) {
            return { ...g, score: value };
        }
        return g;
    });
    onChange({ ...data, qualificationGroups: newGroups });
  };

  const removeSubjectEntry = (groupId: string, entryId: string) => {
      const newGroups = qualificationGroups.map(g => {
          if (g.id === groupId) {
              if (!g.entries) return g; 
              const newEntries = g.entries.filter(e => e.id !== entryId);
              return { ...g, entries: newEntries };
          }
          return g;
      });
      onChange({ ...data, qualificationGroups: newGroups.filter(g => g.entries!.length > 0) });
  };

  // --- Validation ---
  const isOK = qualificationGroups.length === 0 || qualificationGroups.every(g => {
    const details = getQualificationDetails(g.type);
    
    // Guard against null details object
    if (!details) return false;

    if ((details.format === 'single-score' || details.format === 'test-score')) {
        // Check if min/max are defined for this specific test-score type
        if (details.min !== undefined && details.max !== undefined) {
            const scoreNum = parseFloat(g.score || '');
            return !isNaN(scoreNum) && scoreNum >= details.min && scoreNum <= details.max;
        }
        // If no min/max, just check that a score is present (for tests like STEP)
        return !!g.score;
    }

    if (details.format === 'subject-grade' || details.format === 'multi-component') {
        if (!g.entries) {
            return false; // If there are no entries, it's not valid.
        }
        return g.entries.length > 0 && g.entries.every(e => e.subject && e.grade);
    }
    return true; // Default to valid if format is unknown
});

  useEffect(() => { onValidityChange(isOK); }, [isOK, onValidityChange]);


  return (
    // <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

    <div style={{ width: '100%' }}>
      {/* Top section to add a new qualification type */}
      <AddSection>
        <QualificationsDropdown
          value={typeToAdd}
          options={qualificationOptions}
          onChange={setTypeToAdd}
        />
        <LightButton onClick={addGroup} css={{ flexGrow: 1 }}>
          <Plus size={15} style={{ marginRight: '0.5rem' }} />
          Add Qualification
        </LightButton>
      </AddSection>

      {/* Render each added qualification group */}
      {qualificationGroups.map(group => {
        const details = getQualificationDetails(group.type);
        const isSingleScore = (details?.format === 'single-score' || details?.format === 'test-score');
        const ibTotal = group.type === 'IB Diploma' ? calculateIbTotal(group) : null;
        return (
            <GroupContainer key={group.id}>
              <GroupHeader>
                  <GroupTitle>{group.type.replace(' / IGCSE', '')}</GroupTitle>
                  {/* For single-score types, a group-level delete button is more appropriate */}
                  {isSingleScore && (
                       <ActionButton intent="background" onClick={() => removeGroup(group.id)} aria-label="Remove qualification">
                          <Trash2 size={16} stroke="red"/>
                      </ActionButton>
                  )}
              </GroupHeader>
              <div style={{marginBottom: '2rem'}}></div>
              
              {isSingleScore ? (
                  // RENDER SINGLE SCORE INPUT
                  <Field name={`score-${group.id}`}>
                      <Control
                          type={details.min !== undefined ? "number" : "text"} // Use number input if min/max are defined
                          placeholder={details.min !== undefined ? `Score (e.g. ${details.min} - ${details.max})` : 'Enter Score'}
                          value={group.score || ''}
                          onChange={e => updateSingleScore(group.id, e.target.value)}
                          min={details.min}
                          max={details.max}
                      />
                  </Field>
              ) : (
          <>
          <EntryRow css={{  justifyItems: 'start', fontWeight: 'bold'}}>
            <Label as="span">Subject</Label>
            <Label as="span">Grade </Label>
            <span /> {/* Add this empty placeholder */}

          </EntryRow>

          {(group.entries ?? []).map(entry => {
            const subjectOptions = getSubjectOptions(group.type);
            const gradeOptions = getGradeOptions(group.type);

            return (
            <Field key={entry.id} name={`qual-${entry.id}`}>
                <EntryRow>
                {subjectOptions.length > 0 ? (
                    <SubjectCombobox
                    mode="single"
                    placeholder="Search or select…"
                    value={entry.subject}
                    onSelect={(newValue) => updateSubjectEntry(group.id, entry.id, 'subject', newValue)}
                    options={subjectOptions}
                    />
                ) : (
                    <Control type="text" placeholder="e.g. Maths" value={entry.subject} onChange={e => updateSubjectEntry(group.id, entry.id, 'subject', e.target.value)} />
                )}

                {gradeOptions.length > 0 ? (
                    <StyledDropdown
                      placeholder="Select..."
                      value={entry.grade}
                      onChange={(newValue) => updateSubjectEntry(group.id, entry.id, 'grade', newValue)}
                      options={gradeOptions}
                    />
                  ) : (
                    <Control type="text" placeholder="Enter Score" value={entry.grade} onChange={e => updateSubjectEntry(group.id, entry.id, 'grade', e.target.value)} />
                  )}

                <ActionButton intent="background" onClick={() => removeSubjectEntry(group.id, entry.id)} aria-label="Remove subject">
                  <Trash2 size={16} stroke="red"/>
                </ActionButton>
              </EntryRow>
            </Field>
            );
            })}
            <LightButton onClick={() => addSubjectToGroup(group.id)} css={{marginTop: '1rem'}}>
                <Plus size={15} /> Add Subject
            </LightButton>
            {ibTotal && (
                    <TotalScoreContainer>
                        <TotalScoreLabel>Total Score:</TotalScoreLabel>
                        <TotalScoreValue>{ibTotal.score ?? ''}</TotalScoreValue>
                        <TotalScoreBreakdown>{ibTotal.message}</TotalScoreBreakdown>
                    </TotalScoreContainer>
                )}
                </>
              )}
        </GroupContainer>
        )
      })}
    </div>
  );
};
